import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useAutomationTemplateQuery } from '../../../../../queries/Automation/queries/useAutomationTemplateQuery';
import { useAutomationTemplateStepSwitchUpdateCaseMutation } from '../../../../../queries/Automation/mutations/switchCases/useAutomationTemplateStepSwitchUpdateCaseMutation';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { TextInput } from '../../../../components/TextInput';
import { styleSheet } from '../../../../components/automation/AutomationStepCard/styles';
import { AutomationPreview } from '../../../../components/automation/AutomationPreview';
import { useAutoSave } from '../../hooks/useAutoSave';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { renderFilterCriteria } from '../../utils/filterDisplay';
import { switchStepStyles, getCaseBorderColor } from './styles/switchStepRendererStyles';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';

interface SwitchStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

const CASE_NAME_DEFAULT = 'Default';
const CASE_NAME_PREFIX = 'Case';

export function SwitchStepRenderer({ nodeId: _nodeId, data, readOnly: readOnlyProp }: SwitchStepRendererProps) {
	const {
		step: switchStep,
		template,
		readonly,
		errorMessages,
	} = useStepRenderer<Api.ISwitchAutomationStep>(data, readOnlyProp);

	const { impersonationContext } = useAutomationFlowContext();

	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	if (!switchStep) {
		return <div>Loading step data… Try collapsing and re-expanding this node.</div>;
	}

	const cases = switchStep.cases || [];

	return (
		<div className={css(switchStepStyles.container)}>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='detailed'
				lastSavedAt={autoSave.lastSavedAt}
				errorMessage={autoSave.errorMessage}
				retryCount={autoSave.retryCount}
			/>
			<div className={css(styleSheet.fieldLabel)}>Conditional Branch</div>
			<div className={css(switchStepStyles.infoSection)}>
				<div className={css(switchStepStyles.infoText)}>
					This step creates different paths based on conditions. Each case below represents a branch.
				</div>
				{cases.length === 0 ? (
					<div className={css(switchStepStyles.emptyCasesText)}>
						No cases defined. Add cases to create conditional branches.
					</div>
				) : (
					<div className={css(switchStepStyles.casesContainer)}>
						{cases.map((caseStatement: Api.IAutomationStepCaseStatement, index: number) => {
							const isDefault = caseStatement.isDefault;
							const caseName = isDefault ? CASE_NAME_DEFAULT : caseStatement.name || `${CASE_NAME_PREFIX} ${index + 1}`;

							return (
								<CaseBranch
									key={caseStatement.id || `case-${index}`}
									caseStatement={caseStatement}
									caseName={caseName}
									caseTemplateId={caseStatement.automationTemplateId}
									isDefault={isDefault}
									readonly={readonly}
									caseIndex={index}
									templateId={template?.id}
									stepId={data.stepId}
									impersonationContext={impersonationContext}
								/>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

interface CaseBranchProps {
	caseStatement: Api.IAutomationStepCaseStatement;
	caseName: string;
	caseTemplateId?: string;
	isDefault: boolean;
	readonly: boolean;
	caseIndex: number;
	templateId?: string;
	stepId?: string;
	impersonationContext?: Api.IImpersonationContext;
}

function CaseBranch({
	caseStatement,
	caseName: initialCaseName,
	caseTemplateId,
	isDefault,
	readonly,
	caseIndex,
	templateId,
	stepId,
	impersonationContext,
}: CaseBranchProps) {
	const errorMessages = useErrorMessages();
	const [isEditing, setIsEditing] = React.useState(false);
	const [caseName, setCaseName] = React.useState(initialCaseName);

	const updateCaseMutation = useAutomationTemplateStepSwitchUpdateCaseMutation({
		onSuccess: () => {
			setIsEditing(false);
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const { data: childTemplate, isLoading } = useAutomationTemplateQuery({
		id: caseTemplateId,
		enabled: !!caseTemplateId,
	});

	const filter = caseStatement.filter;
	const borderColor = getCaseBorderColor(caseIndex, isDefault);
	const hasFilter = !isDefault && !!filter;

	const childNodeCount = React.useMemo(() => {
		if (!childTemplate) {
			return 0;
		}
		const steps = childTemplate.draft?.steps || childTemplate.published?.steps;
		return steps?.length || 0;
	}, [childTemplate]);

	const handleNameBlur = () => {
		if (!isEditing || !templateId || !stepId || !caseStatement.id) {
			setIsEditing(false);
			return;
		}

		if (caseName !== initialCaseName && caseName.trim()) {
			updateCaseMutation.mutate({
				templateId,
				stepId,
				caseId: caseStatement.id,
				caseData: {
					...caseStatement,
					name: caseName.trim(),
				},
				impersonationContext,
			});
		} else {
			setCaseName(initialCaseName);
			setIsEditing(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleNameBlur();
		} else if (e.key === 'Escape') {
			setCaseName(initialCaseName);
			setIsEditing(false);
		}
	};

	const canEdit = !readonly && !isDefault && !!templateId && !!stepId && !!caseStatement.id;

	return (
		<div className={css(switchStepStyles.caseCard)} style={{ borderLeftColor: borderColor }}>
			<div className={css(switchStepStyles.caseHeader)}>
				<span
					className={css(
						switchStepStyles.caseBadge,
						isDefault ? switchStepStyles.caseBadgeDefault : switchStepStyles.caseBadgeNormal
					)}
					style={{ backgroundColor: borderColor }}
				>
					{isDefault ? 'DEFAULT' : `Segment #${caseIndex + 1}`}
				</span>
				{isEditing ? (
					<TextInput
						type='text'
						className={css(switchStepStyles.caseNameInput)}
						value={caseName}
						onChange={e => setCaseName(e.target.value)}
						onBlur={handleNameBlur}
						onKeyDown={handleKeyDown}
						autoFocus
						disabled={updateCaseMutation.isPending}
					/>
				) : (
					<span
						className={css(switchStepStyles.caseName, canEdit && switchStepStyles.caseNameEditable)}
						onClick={canEdit ? () => setIsEditing(true) : undefined}
						title={canEdit ? 'Click to edit name' : undefined}
					>
						{caseName}
					</span>
				)}
				{childNodeCount > 0 && (
					<span className={css(switchStepStyles.caseNodeCount)} title={`${childNodeCount} steps in this branch`}>
						{childNodeCount} {childNodeCount === 1 ? 'step' : 'steps'}
					</span>
				)}
			</div>

			{hasFilter && renderFilterCriteria(filter)}

			{isLoading ? (
				<div
					className={css(hasFilter ? switchStepStyles.loadingContainerWithFilter : switchStepStyles.loadingContainer)}
				>
					<LoadingSpinner type='small' />
				</div>
			) : childTemplate ? (
				<div
					className={css(
						hasFilter ? switchStepStyles.templatePreviewContainerWithFilter : switchStepStyles.templatePreviewContainer
					)}
				>
					<AutomationPreview
						templateId={childTemplate?.id}
						showPublished={false}
						impersonationContext={impersonationContext}
					/>
				</div>
			) : (
				<div className={css(hasFilter ? switchStepStyles.noTemplateTextWithFilter : switchStepStyles.noTemplateText)}>
					No template assigned to this case.
				</div>
			)}
		</div>
	);
}
